/**
 * Where the refresh token is kept between visits.
 *
 * The access token (15 min) lives only in memory. The refresh token (30 days,
 * rotated on every use) must survive a reload, so it goes in storage that
 * JavaScript can read: localStorage on the web, and later the Capacitor
 * Preferences / secure-storage plugin in the apps (swap the store with
 * `setTokenStore`). We use bearer tokens instead of httpOnly cookies because
 * cookies don't work across origins in native apps. The cost is that an XSS
 * bug could read the refresh token; the mitigations are rotation with reuse
 * detection on the server (a stolen token used twice signs everyone out), the
 * short access lifetime, and no third-party scripts (see docs/web.md).
 */
export interface TokenStore {
  getRefresh(): string | null;
  setRefresh(token: string | null): void;
}

const KEY = "rai.refresh";
const REMEMBER = "rai.remember"; // "0" = the user unticked "Keep me signed in"

function remembered(): boolean {
  try {
    return localStorage.getItem(REMEMBER) !== "0";
  } catch {
    return true;
  }
}

/** "Keep me signed in": ticked (the default) keeps the token for its 30 days;
 * unticked keeps it only for this browser session (sessionStorage). */
export function setRemember(remember: boolean): void {
  try {
    if (remember) localStorage.removeItem(REMEMBER);
    else localStorage.setItem(REMEMBER, "0");
  } catch {
    // storage blocked: nothing to remember
  }
}

export const localStorageStore: TokenStore = {
  getRefresh() {
    try {
      return localStorage.getItem(KEY) ?? sessionStorage.getItem(KEY);
    } catch {
      return null;
    }
  },
  setRefresh(token) {
    try {
      localStorage.removeItem(KEY);
      sessionStorage.removeItem(KEY);
      if (token) (remembered() ? localStorage : sessionStorage).setItem(KEY, token);
    } catch {
      // storage blocked (private mode): the session lasts until reload
    }
  },
};

export function memoryStore(initial: string | null = null): TokenStore {
  let value = initial;
  return {
    getRefresh: () => value,
    setRefresh: (t) => {
      value = t;
    },
  };
}

let store: TokenStore = localStorageStore;

export function getTokenStore(): TokenStore {
  return store;
}

export function setTokenStore(next: TokenStore): void {
  store = next;
}
